import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { initializeApp as initFirebase } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { registerPushToken, reactToMessageOnServer, markConversationAsReadOnServer, addMessageToConversationOnServer } from '../api';
import { PageState } from '../types';
import { LocalNotifications } from '@capacitor/local-notifications';

let activeRingtone: { stop: () => void } | null = null;
export const playRingtone = () => {
    if (activeRingtone) return;
    try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const osc1 = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const gainNode = ctx.createGain();

        osc1.type = 'sine';
        osc1.frequency.value = 440;
        osc2.type = 'sine';
        osc2.frequency.value = 480;

        gainNode.gain.setValueAtTime(0, ctx.currentTime);
        
        let t = ctx.currentTime;
        for (let i = 0; i < 5; i++) {
            gainNode.gain.setValueAtTime(0.3, t);
            gainNode.gain.setValueAtTime(0, t + 2);
            t += 4;
        }

        osc1.connect(gainNode);
        osc2.connect(gainNode);
        gainNode.connect(ctx.destination);

        osc1.start();
        osc2.start();

        activeRingtone = {
            stop: () => {
                try {
                    osc1.stop();
                    osc2.stop();
                    ctx.close();
                } catch (e) {}
                activeRingtone = null;
            }
        };
    } catch (err) {
        console.error('Failed to play ringtone:', err);
    }
};

export const stopRingtone = () => {
    if (activeRingtone) {
        activeRingtone.stop();
    }
};

const firebaseConfig = {
    apiKey: "AIzaSyAcLX0QqTz7_2Y0WzeG3WAfgyL-gn586gw",
    authDomain: "goodlyfe-2a70e.firebaseapp.com",
    projectId: "goodlyfe-2a70e",
    storageBucket: "goodlyfe-2a70e.firebasestorage.app",
    messagingSenderId: "190689134320",
    appId: "1:190689134320:web:e65269c4c4344c84886435",
    measurementId: "G-5Z66DGH8ZE"
};

const VAPID_KEY = "BH0YGCl_jurDp7e7aFlOBZ2__vLaAl-RPiXzaIFz6uh63-sgVglUyuRsWcKbm4fMrJTIy1tYGqSFYcy_WqYmsnE";

let firebaseApp: any;
let messaging: any;

try {
    if (firebaseConfig.apiKey && firebaseConfig.apiKey !== "YOUR_API_KEY") {
        firebaseApp = initFirebase(firebaseConfig);
        if (Capacitor.getPlatform() === 'web' && 'serviceWorker' in navigator) {
            import('firebase/messaging').then(({ isSupported }) => {
                isSupported().then(supported => {
                    if (supported) {
                        messaging = getMessaging(firebaseApp);
                    } else {
                        console.log('[Push] Firebase Messaging not supported in this browser');
                    }
                });
            });
        }
    }
} catch (error) {
    console.error('[Firebase] Init Error:', error);
}

// Map FCM data to a PageState object
const fcmDataToPageState = (data: any): PageState | null => {
    if (!data) return null;
    
    const type = data.type;
    const fromUser = data.fromUser;
    const postId = data.postId;
    const communityId = data.communityId;
    const channelId = data.channelId;
    const messageId = data.messageId;

    if (postId) {
        return { type: 'post', postId: postId.toString() };
    }

    if (type === 'message' && fromUser) {
        return { type: 'messages', username: fromUser };
    }

    if (type === 'link') {
        // Navigate to the sender's DM, falling back to notifications if no sender info
        if (fromUser) {
            return { type: 'messages', username: fromUser };
        }
        return { type: 'notifications' };
    }

    if (type === 'follow' && fromUser) {
        return { type: 'profile', userId: fromUser };
    }

    if (type === 'vouch' && fromUser) {
        return { type: 'profile', userId: fromUser };
    }

    if (communityId) {
        return { 
            type: 'community_detail', 
            query: communityId, 
            channelId: channelId || undefined,
            messageId: messageId || undefined
        };
    }

    if (type === 'system') {
        return { type: 'home' };
    }

    return null;
};

let isSettingUp = false;
let listenersAttached = false;
let activeUsername = '';
let onNotificationAction: ((page: PageState, recipient?: string) => void) | null = null;

export const setupPushNotifications = async (
    username: string, 
    navigateToPage?: (page: PageState, recipient?: string) => void, 
    userInitiated = true
) => {
    if (navigateToPage) {
        onNotificationAction = navigateToPage;
    }
    activeUsername = username;

    if (isSettingUp) {
        console.log('[Push] Setup already in progress, skipping duplicate call');
        return;
    }
    isSettingUp = true;
    console.log('[Push] Starting setupPushNotifications for:', username);

    try {
        console.log('[Push] Heartbeat 1: Native check...');
        if (Capacitor.isNativePlatform()) {
            console.log('[Push] Heartbeat 2: Entering Native block...');

            if (!listenersAttached) {
                await FirebaseMessaging.addListener('apnsTokenReceived', async (event: { token: string }) => {
                    console.log('[Push] Native APNs token received:', event.token);
                    try {
                        console.log('[Push] Calling FirebaseMessaging.getToken() after APNs...');
                        const currentToken = await FirebaseMessaging.getToken();
                        if (currentToken && currentToken.token && activeUsername) {
                            console.log('[Push] Native FCM token received after APNs:', currentToken.token);
                            await registerPushToken(activeUsername, currentToken.token);
                        }
                    } catch (err) {
                        console.error('[Push] Error getting FCM token after APNs:', err);
                    }
                });

                await FirebaseMessaging.addListener('tokenReceived', async (event: { token: string }) => {
                    console.log('[Push] Native FCM token received via listener:', event.token);
                    if (event.token && activeUsername) {
                        try {
                            await registerPushToken(activeUsername, event.token);
                            console.log('[Push] Successfully registered token with server');
                        } catch (err) {
                            console.error('[Push] Failed to register token with server:', err);
                        }
                    }
                });

                await FirebaseMessaging.addListener('notificationReceived', async (event: any) => {
                    console.log('[Push] Foreground notification received:', JSON.stringify(event.notification, null, 2));
                    const data = event.notification?.data;
                    if (data && data.type === 'dm_call') {
                        playRingtone();
                        
                        // Schedule local notification with action buttons in foreground
                        await LocalNotifications.schedule({
                            notifications: [{
                                id: Date.now(),
                                title: event.notification.title || 'Incoming Call',
                                body: event.notification.body || 'Someone is calling you...',
                                actionTypeId: 'DM_CALL_INCOMING',
                                extra: {
                                    conversationId: data.conversationId,
                                    channelId: data.channelId,
                                    recipient: data.recipient,
                                    serverUrl: data.serverUrl
                                }
                            }]
                        });
                    } else if (data && data.type === 'dm_call_timeout') {
                        stopRingtone();
                    }
                });

                await FirebaseMessaging.addListener('notificationActionPerformed', async (event: any) => {
                    console.log('[Push] Notification action performed:', JSON.stringify(event, null, 2));
                    const data = event.notification?.data;
                    const actionId = event.actionId;
                    const inputValue = event.inputValue;

                    if (actionId) {
                        console.log(`[Push] Action triggered: ${actionId}, input: ${inputValue}`);
                        const conversationId = data?.conversationId ? parseInt(data.conversationId) : null;
                        const messageId = data?.messageId ? parseInt(data.messageId) : null;
                        const recipient = data?.recipient;

                        if (actionId === 'like' && conversationId && messageId && recipient) {
                            try {
                                await reactToMessageOnServer(conversationId, messageId, '❤️', recipient);
                                console.log('[Push] Automatically liked message from push notification');
                            } catch (err) {
                                console.error('[Push] Failed to auto-like message:', err);
                            }
                        } else if (actionId === 'read' && conversationId && recipient) {
                            try {
                                await markConversationAsReadOnServer(conversationId, recipient);
                                console.log('[Push] Automatically marked conversation as read from push notification');
                            } catch (err) {
                                console.error('[Push] Failed to auto-read message:', err);
                            }
                        } else if (actionId === 'reply' && conversationId && recipient && inputValue) {
                            try {
                                const newMessage = {
                                    id: Date.now(),
                                    senderId: recipient,
                                    text: inputValue,
                                    timestamp: new Date().toISOString()
                                };
                                await addMessageToConversationOnServer(conversationId, newMessage);
                                console.log('[Push] Automatically replied to message from push notification:', inputValue);
                            } catch (err) {
                                console.error('[Push] Failed to auto-reply to message:', err);
                            }
                        } else if (actionId === 'accept_call' && conversationId) {
                            stopRingtone();
                            if (onNotificationAction) {
                                onNotificationAction({
                                    type: 'community_detail',
                                    query: conversationId.toString(),
                                    channelId: data?.channelId || 'dm'
                                }, recipient);
                            }
                        } else if (actionId === 'decline_call' && conversationId && recipient) {
                            stopRingtone();
                            try {
                                const serverUrl = data?.serverUrl || 'https://goodlyfe.net';
                                await fetch(`${serverUrl}/api/call/decline`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ conversationId, recipient })
                                });
                                console.log('[Push] Successfully declined call from action');
                            } catch (err) {
                                console.error('[Push] Failed to decline call from action:', err);
                            }
                        }
                    }

                    if (data && onNotificationAction) {
                        const pageState = fcmDataToPageState(data);
                        if (pageState) {
                            const recipient = data.recipient;
                            console.log('[Push] Navigating to page from action:', pageState, 'Recipient:', recipient);
                            onNotificationAction(pageState, recipient);
                        }
                    }
                });

                listenersAttached = true;
                console.log('[Push] All native listeners attached successfully');
            }

            // Android-specific channel creation
            if (Capacitor.getPlatform() === 'android') {
                try {
                    console.log('[Push] Android detected. Creating channel...');
                    await FirebaseMessaging.createChannel({
                        id: 'default',
                        name: 'Default',
                        description: 'Default notification channel',
                        importance: 5,
                        visibility: 1,
                        sound: 'default'
                    });

                    await FirebaseMessaging.createChannel({
                        id: 'ringer',
                        name: 'Incoming Call',
                        description: 'Incoming voice/video call ringtone',
                        importance: 5,
                        visibility: 1,
                        sound: 'ringtone',
                        vibration: true
                    });
                } catch (e) {
                    console.error('[Push] Channel creation error:', e);
                }
            }

            // Permissions with a larger delay to allow other plugins (like Capgo) to finish
            console.log('[Push] Heartbeat 3: Waiting 5s for native bridge to clear...');
            await new Promise(resolve => setTimeout(resolve, 5000));
            
            console.log("[Push] Calling FirebaseMessaging.checkPermissions()...");
            
            // Timeout wrapper for native call
            const checkPermissionsWithTimeout = () => {
                return Promise.race([
                    FirebaseMessaging.checkPermissions(),
                    new Promise<never>((_, reject) => 
                        setTimeout(() => reject(new Error('checkPermissions timed out after 20s')), 20000)
                    )
                ]);
            };

            let permStatus;
            try {
                permStatus = await checkPermissionsWithTimeout();
                console.log('[Push] Initial permission status:', permStatus.receive);
            } catch (err: any) {
                console.error('[Push] checkPermissions failed or timed out:', err.message || err);
                isSettingUp = false;
                return;
            }

            if (permStatus.receive === 'prompt' && userInitiated) {
                console.log('[Push] Calling FirebaseMessaging.requestPermissions()...');
                permStatus = await FirebaseMessaging.requestPermissions();
                console.log('[Push] Request result:', permStatus.receive);
            }

            console.log('[Push] Branch check: status is', permStatus.receive);

            if (permStatus.receive !== 'granted') {
                console.warn('[Push] Native permissions not granted:', permStatus.receive);
                return;
            }
            
            console.log('[Push] Permissions confirmed as granted, proceeding to token acquisition...');

            // Initial token acquisition
            const tryGetToken = async (retryCount = 0) => {
                console.log(`[Push] tryGetToken attempt ${retryCount + 1}...`);
                try {
                    console.log(`[Push] Calling FirebaseMessaging.getToken() (attempt ${retryCount + 1})...`);
                    const currentToken = await FirebaseMessaging.getToken();
                    if (currentToken && currentToken.token && activeUsername) {
                        console.log('[Push] Current Native FCM token successfully acquired:', currentToken.token.substring(0, 10) + '...');
                        await registerPushToken(activeUsername, currentToken.token);
                    } else {
                        console.warn(`[Push] No token returned from getToken (attempt ${retryCount + 1})`);
                        if (retryCount < 5) {
                            setTimeout(() => tryGetToken(retryCount + 1), 3000);
                        }
                    }
                } catch (err: any) {
                    console.error(`[Push] Error in getToken (attempt ${retryCount + 1}):`, err.message || err);
                    if (retryCount < 5) {
                        setTimeout(() => tryGetToken(retryCount + 1), 3000);
                    }
                }
            };

            await tryGetToken();
        } else {
            // Web Logic
            if (!messaging) {
                console.warn('[Push] Firebase messaging not initialized for web');
                return;
            }
            try {
                const token = await getToken(messaging, { vapidKey: VAPID_KEY });
                if (token) {
                    console.log('[Push] Web token:', token);
                    await registerPushToken(username, token);
                }

                onMessage(messaging, (payload: any) => {
                    console.log('[Push] Web received:', payload);
                    if (payload.notification && typeof Notification !== 'undefined') {
                        new Notification(payload.notification.title || 'Notification', {
                            body: payload.notification.body,
                            icon: '/logo192.png'
                        });
                    }
                });
            } catch (error) {
                console.error('[Push] Web setup error:', error);
            }
        }
    } catch (error: any) {
        console.error('[Push] Critical setup error:', error);
    } finally {
        isSettingUp = false;
        console.log('[Push] Setup attempt completed');
    }
};

export const requestAppPermissions = async (
    navigateToPage: (page: PageState, recipient?: string) => void, 
    username?: string
) => {
    if (username) {
        setupPushNotifications(username, navigateToPage, true).catch(err => console.error('[Push] Final setup error:', err));
    }
};

export const getCurrentToken = async () => {
    try {
        if (Capacitor.isNativePlatform()) {
            const tokenResult = await FirebaseMessaging.getToken();
            return tokenResult.token;
        } else if (messaging) {
            return await getToken(messaging, { vapidKey: VAPID_KEY });
        }
    } catch (err) {
        console.error('[Push] Error getting current token:', err);
    }
    return null;
};
